import axios from 'axios';

const getBaseUrl = () => {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env) return env;
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return '';
  }
  return 'https://scrumtherapistserver.onrender.com';
};

const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 60000
});

export const BLOG_API_BASE_URL = getBaseUrl();
let _lastSource = 'live';
export const getLastSource = () => _lastSource;
export const setLastSource = (s) => { _lastSource = s; };

let _lastError = null;
export const getLastError = () => _lastError;
const setLastError = (e) => {
  if (!e) { _lastError = null; return; }
  const status = e.response?.status || (e.code === 'ECONNABORTED' ? 'TIMEOUT' : (e.isAxiosError ? 'NETWORK' : 'UNKNOWN'));
  const url = e.config?.url || '';
  _lastError = { status, message: e.message || String(e), url };
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function withRetry(fn, retries = 1, retryDelayMs = 5000) {
  let lastErr = null;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      const isRetryable = !status || status >= 500 || err.code === 'ECONNABORTED' || (err.isAxiosError && !status);
      if (i >= retries || !isRetryable) break;
      await sleep(retryDelayMs);
    }
  }
  throw lastErr;
}

const FALLBACK_BLOGS = [
  {
    id: 1,
    title: 'Effective Daily Standups: Beyond the Three Questions',
    excerpt: 'Learn how to make your daily standups more effective and engaging for the team...',
    image: `${process.env.PUBLIC_URL}/images/pic1.png`,
    date: '2023-11-15',
    author: 'Jenny.J',
    tags: ['Daily Standup', 'Scrum Meetings', 'Team Collaboration'],
    content: `
      <p>Daily standups are a cornerstone of the Scrum framework, designed to synchronize the team's activities and identify impediments quickly. However, many teams fall into the trap of treating this ceremony as a mundane status report.</p>
      <h2>The Traditional Format</h2>
      <p>The standard three questions are: What did I accomplish yesterday? What will I work on today? Are there any impediments?</p>
      <h2>Moving Beyond the Basics</h2>
      <h3>1. Focus on the Sprint Goal</h3>
      <p>Reiterate the sprint goal at the start to keep everyone aligned.</p>
      <h3>2. Walk the Board, Not the Room</h3>
      <p>Focus on work items rather than individuals to promote collective ownership.</p>
      <h2>Conclusion</h2>
      <p>Use ScrumTherapist tools to make standups more energizing and effective.</p>
    `,
    authorTitle: 'Certified Scrum Master & Agile Coach',
    authorImage: `${process.env.PUBLIC_URL}/images/pic3.png`,
    seoDescription: 'Practical strategies to transform your daily standups from boring status reports into powerful team collaboration sessions.'
  },
  {
    id: 2,
    title: 'Sprint Planning: Setting Your Team Up for Success',
    excerpt: 'Discover key strategies to make your sprint planning sessions more productive...',
    image: `${process.env.PUBLIC_URL}/images/pic4.png`,
    date: '2023-10-28',
    author: 'Jenny.J',
    tags: ['Sprint Planning', 'Agile', 'Product Backlog'],
    content: `
      <p>Effective sprint planning is the foundation of a successful sprint.</p>
      <h2>The Fundamentals</h2>
      <p>Sprint planning has two parts: What and How.</p>
      <h2>Strategies for More Effective Sprint Planning</h2>
      <h3>1. Pre-Planning Preparation</h3>
      <p>Refine and prioritize backlog items before the meeting.</p>
      <h3>2. Start with the Sprint Goal</h3>
      <p>Craft a clear sprint goal that gives the team a shared purpose.</p>
      <h2>Conclusion</h2>
      <p>Tune your planning with ScrumTherapist estimation and retrospective tools.</p>
    `,
    authorTitle: 'Certified Scrum Master & Agile Coach',
    authorImage: `${process.env.PUBLIC_URL}/images/pic3.png`,
    seoDescription: 'Key strategies and best practices to run effective sprint planning sessions that set your Scrum team up for success.'
  },
  {
    id: 3,
    title: 'Retrospectives That Drive Real Change',
    excerpt: 'Turn your retrospectives from complaint sessions into catalysts for improvement...',
    image: `${process.env.PUBLIC_URL}/images/pic2.png`,
    date: '2023-10-10',
    author: 'Jenny.J',
    tags: ['Retrospective', 'Continuous Improvement', 'Team Dynamics'],
    content: `
      <p>Sprint retrospectives are one of the most powerful tools for continuous improvement.</p>
      <h2>The Retrospective Challenge</h2>
      <p>Common issue: discussion without action.</p>
      <h2>Principles for Effective Retrospectives</h2>
      <h3>1. Psychological Safety First</h3>
      <p>Team members must feel safe to share honestly.</p>
      <h3>2. Focus on Actionable Outcomes</h3>
      <p>Pick 1-2 experiments with owners and deadlines.</p>
      <h2>Conclusion</h2>
      <p>Run engaging retrospectives and track actions with ScrumTherapist.</p>
    `,
    authorTitle: 'Certified Scrum Master & Agile Coach',
    authorImage: `${process.env.PUBLIC_URL}/images/pic3.png`,
    seoDescription: 'How to run retrospectives that actually lead to real improvement and change in your Agile and Scrum teams.'
  },
  {
    id: 4,
    title: 'Product Owner vs Scrum Master: Understanding the Difference',
    excerpt: 'Clarify the distinct roles and responsibilities of these two critical Scrum positions...',
    image: `${process.env.PUBLIC_URL}/images/pic3.png`,
    date: '2023-09-22',
    author: 'Jenny.J',
    tags: ['Scrum Roles', 'Product Owner', 'Scrum Master'],
    content: `
      <p>Misalignment between PO and SM is one of the most common causes of Scrum failure.</p>
      <h2>Role at a Glance</h2>
      <p>PO manages value and backlog. SM removes impediments and coaches Scrum.</p>
      <h2>Where They Collaborate</h2>
      <p>Both collaborate in planning, refinement, and removing blockers.</p>
      <h2>Conclusion</h2>
      <p>Ask ScrumTherapist Q&A for real-world scenarios to clarify role boundaries.</p>
    `,
    authorTitle: 'Certified Scrum Master & Agile Coach',
    authorImage: `${process.env.PUBLIC_URL}/images/pic3.png`,
    seoDescription: 'Understand the distinct roles and responsibilities of Product Owner and Scrum Master in a Scrum team.'
  }
];

export const blogService = {
  async listBlogs({ status = 'published', limit = 50, skip = 0, tag, search } = {}) {
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      params.set('limit', String(limit));
      params.set('skip', String(skip));
      if (tag) params.set('tag', tag);
      if (search) params.set('search', search);
      const { data } = await withRetry(() => api.get(`/api/blogs?${params.toString()}`));
      if (data?.success && Array.isArray(data.data)) {
        setLastSource('live');
        setLastError(null);
        return data.data.map(mapBlog);
      }
    } catch (err) {
      console.warn('blogService.listBlogs API failed, using fallback', err.message);
      setLastError(err);
    }
    setLastSource('fallback');
    return FALLBACK_BLOGS;
  },

  async getBlogById(id) {
    try {
      const { data } = await withRetry(() => api.get(`/api/blogs/${encodeURIComponent(id)}`));
      if (data?.success && data.data) {
        setLastSource('live');
        setLastError(null);
        return mapBlog(data.data, true);
      }
    } catch (err) {
      console.warn('blogService.getBlogById API failed, using fallback', err.message);
      setLastError(err);
    }
    setLastSource('fallback');
    return mapBlog(FALLBACK_BLOGS.find(b => String(b.id) === String(id)) || FALLBACK_BLOGS[0], true);
  },

  async listTags() {
    try {
      const { data } = await withRetry(() => api.get('/api/blogs/tags'));
      if (data?.success) {
        setLastSource('live');
        setLastError(null);
        return data.data;
      }
    } catch (e) {
      setLastError(e);
    }
    setLastSource('fallback');
    const tags = {};
    FALLBACK_BLOGS.forEach(b => b.tags.forEach(t => { tags[t] = (tags[t] || 0) + 1; }));
    return Object.entries(tags).map(([tag, count]) => ({ tag, count }));
  },

  async triggerGenerate({ keyword, autoPublish = true, count = 1, adminToken } = {}) {
    const headers = adminToken ? { 'x-admin-token': adminToken } : {};
    const { data } = await api.post('/api/admin/blogs/generate', { keyword, autoPublish, count }, { headers });
    return data;
  },

  async getBlogHealth() {
    try {
      const { data } = await withRetry(() => api.get('/api/blogs/generate/health'), 0);
      if (data && typeof data === 'object') {
        return { ok: true, data };
      }
    } catch (e) {
      return { ok: false, error: e && e.message ? e.message : String(e) };
    }
    return { ok: false, error: 'Unknown' };
  }
};

function mapBlog(b, withDetail = false) {
  if (!b) return b;
  const id = b.id || b._id;
  return {
    id,
    _id: b._id || id,
    slug: b.slug ? String(b.slug) : String(id),
    title: b.title,
    excerpt: b.excerpt,
    image: normalizeImage(b.image),
    date: b.date || b.publishedAt || b.createdAt,
    author: b.author,
    authorTitle: b.authorTitle,
    authorImage: normalizeImage(b.authorImage),
    tags: Array.isArray(b.tags) ? b.tags : [],
    seoDescription: b.seoDescription || b.excerpt,
    keywords: b.keywords || [],
    viewCount: b.viewCount || 0,
    status: b.status || 'published',
    aiGenerated: !!b.aiGenerated,
    ...(withDetail ? { content: b.content || '' } : {})
  };
}

function normalizeImage(img) {
  if (!img) return `${process.env.PUBLIC_URL}/images/pic1.png`;
  if (img.startsWith('http') || img.startsWith('data:')) return img;
  if (img.startsWith('/images') || img.startsWith('/logo')) return `${process.env.PUBLIC_URL}${img}`;
  return img;
}
